import { useMutation } from '@tanstack/react-query';
import {
  AnalyticsAuthEventNames,
  AnalyticsAuthMode,
  trackAuthEvent,
} from '@tectonic/analytics';
import { getErrorMessage } from '@tectonic/errors';
import { signUpWithEmailPassword } from '@tectonic/remix-client-network';
import { delay } from '@tectonic/utils';
import { isEmpty } from 'lodash-es';
import { useToast } from '../../../core/ElemasonEntry/Toast';
import { useAccessorValue } from '../../../hooks/useAccessorValue';
import {
  enrichAnalyticsPayloadWithWidgetData,
  setupAnalyticsUser,
} from '../../../utils';

import type {
  ElemasonAuthEmailPasswordSignUpFormWidget,
  User,
  UserSignUpWithEmailPasswordPayload,
} from '@tectonic/types';
import type { AuthEmailPasswordSignUpFormValue } from '../../../components';

const signUpMutationFn = async (
  payload: UserSignUpWithEmailPasswordPayload
) => {
  const response = await signUpWithEmailPassword(payload);
  if (response.error || isEmpty(response.data)) {
    throw response.error;
  }

  return response.data;
};

const toAnalyticsPayload = (
  widget: ElemasonAuthEmailPasswordSignUpFormWidget,
  payload: object
) =>
  enrichAnalyticsPayloadWithWidgetData(
    {
      ...payload,
      mode: AnalyticsAuthMode.EMAIL,
    },
    widget
  );

const useAuthEmailPasswordSignUpForm = (
  widget: ElemasonAuthEmailPasswordSignUpFormWidget
) => {
  const wData = widget.data!;

  const { showToast } = useToast();
  const currentUser = useAccessorValue<User>({
    accessor: '$$globalState.currentUser',
  })!;

  const { isPending: isLoading, mutateAsync: signUp } = useMutation({
    mutationKey: [],
    mutationFn: signUpMutationFn,
    onError: (error, payload) => {
      const aPayload = toAnalyticsPayload(widget, { ...payload, error });
      trackAuthEvent(
        AnalyticsAuthEventNames.SIGN_IN_ERROR,
        enrichAnalyticsPayloadWithWidgetData(aPayload, widget)
      );
    },
    onSuccess: (_, payload) => {
      const aPayload = toAnalyticsPayload(widget, payload);
      trackAuthEvent(
        AnalyticsAuthEventNames.SIGN_IN_SUCCESS,
        enrichAnalyticsPayloadWithWidgetData(aPayload, widget)
      );
    },
    onMutate: (payload) => {
      const aPayload = toAnalyticsPayload(widget, payload);
      trackAuthEvent(
        AnalyticsAuthEventNames.SIGN_IN_REQUEST,
        enrichAnalyticsPayloadWithWidgetData(aPayload, widget)
      );
    },
  });

  const onSubmit = async (payload: AuthEmailPasswordSignUpFormValue) => {
    try {
      const { user: nUser } = await signUp(payload);
      showToast({ title: wData.messages.signUpSuccess });
      setupAnalyticsUser(nUser, currentUser);
      await delay(1000);
      globalThis.location.reload();
    } catch (error) {
      showToast({ title: getErrorMessage(error, wData.errorMessages) });
    }
  };

  return { isLoading, onSubmit };
};

export { useAuthEmailPasswordSignUpForm };
